import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  contrastCheckFgHex,
  isLightFillHex,
  normalizeColorCached,
  type NormalizedColor,
  type NormalizeColorOptions,
} from '@/lib/colorVariants'

export type ColorSwatchItem = {
  value: string
  available: boolean
  /** Données marchand — prioritaire sur le libellé pour la teinte. */
  trustedHex?: string | null
}

type ColorSwatchSelectorProps = {
  /** Option axis label shown to screen readers (e.g. “Couleur”). */
  optionLabel: string
  items: ColorSwatchItem[]
  selectedValue: string | undefined
  onSelect: (value: string) => void
  size?: 'sm' | 'md'
  /** Shown as native tooltip when disabled. */
  unavailableTitle?: string
  className?: string
}

function swatchMeta(value: string, opts?: NormalizeColorOptions): NormalizedColor {
  return normalizeColorCached(value, opts)
}

export function ColorSwatchSelector({
  optionLabel,
  items,
  selectedValue,
  onSelect,
  size = 'md',
  unavailableTitle = 'Indisponible avec cette combinaison',
  className,
}: ColorSwatchSelectorProps) {
  const dim = size === 'sm' ? 'h-9 w-9' : 'h-11 w-11'
  const checkSz = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'

  return (
    <div
      role="listbox"
      aria-label={optionLabel}
      aria-multiselectable={false}
      className={cn('flex flex-wrap gap-x-3 gap-y-4', className)}
    >
      {items.map((item) => {
        const meta = swatchMeta(item.value, { trustedHex: item.trustedHex })
        const active = selectedValue !== undefined && selectedValue === item.value
        const showFill = meta.hex != null && meta.confidence !== 'low'
        const light = isLightFillHex(meta.hex)
        const checkFg = contrastCheckFgHex(meta.hex)
        const indicative = meta.confidence === 'medium'

        const swatchTitle =
          !item.available
            ? unavailableTitle
            : indicative
              ? `${optionLabel}: ${item.value} (teinte indicative)`
              : `${optionLabel}: ${item.value}`

        if (!showFill) {
          return (
            <div key={item.value} className="flex max-w-[7.5rem] flex-col items-stretch gap-1.5 sm:max-w-[8.5rem]">
              <button
                type="button"
                role="option"
                aria-selected={active}
                aria-disabled={!item.available}
                disabled={!item.available}
                title={swatchTitle}
                aria-label={`${optionLabel}: ${item.value}${!item.available ? ` — ${unavailableTitle}` : ''}`}
                onClick={() => item.available && onSelect(item.value)}
                className={cn(
                  'min-h-11 w-full rounded-ref-md border-2 px-2 py-2 text-center text-xs font-semibold leading-snug transition duration-200 ease-out',
                  !item.available
                    ? 'cursor-not-allowed border-border bg-muted text-muted-foreground line-through decoration-primary'
                    : active
                      ? 'border-border bg-foreground text-white shadow-sm'
                      : 'border-border bg-card text-foreground hover:border-border hover:shadow-sm',
                )}
              >
                <span className="line-clamp-3 break-words">{item.value}</span>
              </button>
            </div>
          )
        }

        return (
          <div key={item.value} className="flex w-[4.75rem] flex-col items-center gap-1.5 sm:w-[5.25rem]">
            <button
              type="button"
              role="option"
              aria-selected={active}
              aria-disabled={!item.available}
              disabled={!item.available}
              title={swatchTitle}
              aria-label={`${optionLabel}: ${item.value}${!item.available ? ` — ${unavailableTitle}` : ''}`}
              onClick={() => item.available && onSelect(item.value)}
              className={cn(
                'group relative flex shrink-0 items-center justify-center rounded-full border-2 transition duration-200 ease-out motion-safe:transition-transform',
                dim,
                indicative && 'ring-1 ring-dashed ring-border/60',
                !item.available
                  ? 'cursor-not-allowed opacity-40 grayscale'
                  : 'cursor-pointer hover:z-[1] motion-safe:hover:scale-110 motion-safe:hover:shadow-md',
                light ? 'border-border' : 'border-border/90',
                active && item.available
                  ? 'border-border ring-2 ring-border ring-offset-2 ring-offset-background scale-105 shadow-md [.sf-ui-mode-dark_&]:ring-offset-background'
                  : !item.available
                    ? 'border-border'
                    : 'hover:border-border',
              )}
              style={{ backgroundColor: meta.hex! }}
            >
              {active && item.available ? (
                <span
                  className="pointer-events-none flex h-full w-full items-center justify-center rounded-full"
                  style={{ color: checkFg }}
                  aria-hidden
                >
                  <Check className={cn(checkSz, 'drop-shadow-sm')} strokeWidth={2.5} />
                </span>
              ) : null}
            </button>
            <span
              className={cn(
                'line-clamp-3 w-full max-w-full break-words text-center text-[10px] font-medium leading-snug text-foreground [.sf-ui-mode-dark_&]:text-muted-foreground',
                size === 'md' && 'sm:text-xs',
                !item.available && 'text-muted-foreground line-through decoration-primary',
              )}
              title={item.value}
            >
              {meta.label || item.value}
            </span>
          </div>
        )
      })}
    </div>
  )
}
